import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  DndContext,
  DragStartEvent,
  DragOverlay,
  DragMoveEvent,
  DragEndEvent,
  DragOverEvent,
  DropAnimation,
  defaultDropAnimation,
  UniqueIdentifier,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  buildTree,
  flattenTree,
  getProjection,
  getChildCount,
  removeItem,
  removeChildrenOf,
  setProperty,
} from "./utilities";
import type { FlattenedItem, SensorContext } from "./types";
import { SortableTreeItem } from "./SortableTreeItem";
import { CSS } from "@dnd-kit/utilities";
import useUpdatePart from "@/lib/queries/useUpdatePart";
import PartTreeItem from "./PartTreeItem";
import { Link, useParams } from "react-router";
import { getUrl } from "@/lib/useToPage";
import useModal from "@/lib/modal/useModal";
import ConfirmationModal from "@/lib/modal/ConfirmationModal";
import { useQueryClient } from "@tanstack/react-query";
import $api from "@/lib/queries/openapi";
import { components } from "@/lib/ApiSchema";
import { useFetchRfqNew } from "@/lib/queries/hooks";
import { getFractionalIndex } from "@/lib/utils";

const dropAnimationConfig: DropAnimation = {
  keyframes({ transform }) {
    return [
      { opacity: 1, transform: CSS.Transform.toString(transform.initial) },
      {
        opacity: 0,
        transform: CSS.Transform.toString({
          ...transform.final,
          x: transform.final.x + 5,
          y: transform.final.y + 5,
        }),
      },
    ];
  },
  easing: "ease-out",
  sideEffects({ active }) {
    active.node.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration: defaultDropAnimation.duration,
      easing: defaultDropAnimation.easing,
    });
  },
};

interface Props {
  parts: components["schemas"]["RequestedPartDetailSchema"][];
  canEdit: boolean;
  collapsible?: boolean;
  indentationWidth?: number;
  showAddPart: boolean;
  resetShowAddPart: () => void;
}

const SortablePartTree = forwardRef<SortablePartTreeRef, Props>(
  (
    {
      collapsible,
      indentationWidth = 50,
      showAddPart = false,
      resetShowAddPart,
      parts,
      canEdit,
    }: Props,
    ref,
  ) => {
    const { refetch: refetchRfq } = useFetchRfqNew(); // refetch will update quoteRevision because `useGetCurrentQuoteRevision` now uses same query

    const { updatePart } = useUpdatePart();
    const { teamSlug, rfqId } = useParams();
    const [inputFocusing, setInputFocusing] = useState(false);

    const { showModal, closeModal } = useModal({ onClose: refetchRfq });
    const queryClient = useQueryClient();
    const { mutate: deletePart } = $api.useMutation(
      "delete",
      "/api/teams/{team_slug}/requested_parts/{id}",
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["get", "/api/teams/{team_slug}/rfqs/{id}/full"],
          });
          queryClient.invalidateQueries({
            queryKey: [
              "get",
              "/api/teams/{team_slug}/requested_parts/find_with_operations",
            ],
          });
        },
      },
    );

    function confirmParentChange({
      partId,
      parentId,
      ordinal,
    }: {
      partId: string;
      parentId: string | null;
      ordinal: string;
    }) {
      showModal(
        <ConfirmationModal
          onCancel={() => {
            refetchRfq();
            closeModal();
          }}
          onConfirm={() => {
            updatePart({
              payload: {
                parent_id: parentId,
                ordinal,
              },
              partId,
            });
            closeModal();
          }}
        >
          Are you sure you want to change this parts' relationships?
        </ConfirmationModal>,
      );
    }

    const [items, setItems] = useState(() => parts);
    const newPartId = "new";
    const isFirstRender = useRef(true);

    useEffect(() => {
      const newPart: components["schemas"]["RequestedPartDetailSchema"] = {
        id: newPartId,
        part_number: "",
        revision_number: "",
        description: "",
        cad_model: null,
        technical_drawing: null,
        will_bid: null,
        children: [],
        requested_quantities: [],
        operations: [],
        thumbnail_url: null,
        parent: null,
        note: "",
        selected_process: "Empty",
        should_create_operations_jit: true,
        is_ots: false,
        treat_as_ots: false,
        ordinal: getFractionalIndex(
          // keep it at the end of the list
          parts.length > 0 ? parts[parts.length - 1].ordinal : null,
          null,
        ),
      };

      const itemsCount = flattenTree(parts).length;
      const collapsedParts = parts.map((p) => ({
        ...p,
        collapsed: isFirstRender.current && itemsCount > 6,
      }));
      // this is called two times in dev build due to `StrictMode`, but should work fine in production
      // so dev mode won't start collapsed by default
      isFirstRender.current = false;
      setItems(
        canEdit && showAddPart ? [...collapsedParts, newPart] : collapsedParts,
      );
    }, [parts, canEdit, showAddPart]);

    const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
    const [overId, setOverId] = useState<UniqueIdentifier | null>(null);
    const [offsetLeft, setOffsetLeft] = useState(0);

    const flattenedItems = useMemo(() => {
      const flattenedTree = flattenTree(items);
      const collapsedItems = flattenedTree.reduce<string[]>(
        (acc, { children, collapsed, id }) =>
          collapsed && children.length ? [...acc, id.toString()] : acc,
        [],
      );

      return removeChildrenOf(
        flattenedTree,
        activeId != null ? [activeId, ...collapsedItems] : collapsedItems,
      );
    }, [activeId, items]);

    const projected =
      activeId && overId
        ? getProjection(
            flattenedItems,
            activeId,
            overId,
            offsetLeft,
            indentationWidth,
          )
        : null;
    const sensorContext: SensorContext = useRef({
      items: flattenedItems,
      offset: offsetLeft,
    });

    const sortedIds = useMemo(
      () => flattenedItems.map(({ id }) => id),
      [flattenedItems],
    );
    const activeItem = activeId
      ? flattenedItems.find(({ id }) => id === activeId)
      : null;

    useEffect(() => {
      sensorContext.current = {
        items: flattenedItems,
        offset: offsetLeft,
      };
    }, [flattenedItems, offsetLeft]);

    useImperativeHandle(ref, () => ({ expandOrCollapseAll }));

    const maxDepth = flattenedItems.reduce(
      (max, item) => Math.max(max, item.depth),
      0,
    );

    const isDragDisabled = (id: UniqueIdentifier) => {
      return !canEdit || id === newPartId;
    };

    return (
      <DndContext
        onDragStart={handleDragStart}
        onDragMove={handleDragMove}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <SortableContext
          items={sortedIds}
          strategy={verticalListSortingStrategy}
        >
          {flattenedItems.map((part) => (
            <Link
              key={part.id}
              to={
                inputFocusing || part.id === newPartId
                  ? "#"
                  : getUrl("part", {
                      teamSlug: teamSlug!,
                      rfqId: rfqId!,
                      partId: part.id,
                    })
              }
              onClick={() => inputFocusing && setInputFocusing(false)}
              draggable={false}
              className={`block hover:bg-blue-50 ${inputFocusing ? "cursor-default" : ""}`}
            >
              <SortableTreeItem
                id={part.id}
                disabled={isDragDisabled(part.id)}
                value={
                  <PartTreeItem
                    part={part}
                    setInputFocusing={setInputFocusing}
                    canEdit={canEdit}
                    onCreated={() => resetShowAddPart()}
                    newPartId={newPartId}
                    inverseDepth={maxDepth - part.depth}
                    indentationWidth={indentationWidth}
                  />
                }
                depth={
                  part.id === activeId && projected
                    ? projected.depth
                    : part.depth
                }
                indentationWidth={indentationWidth}
                collapsed={Boolean(part.collapsed && part.children.length)}
                onCollapse={
                  collapsible && part.children.length
                    ? () => handleCollapse(part.id)
                    : undefined
                }
                onRemove={() => {
                  if (!canEdit) return;
                  if (part.id === newPartId) {
                    handleRemove(part.id);
                    resetShowAddPart();
                    return;
                  }

                  showModal(
                    <ConfirmationModal
                      onCancel={closeModal}
                      onConfirm={() => {
                        deletePart({
                          params: {
                            path: {
                              team_slug: teamSlug!,
                              id: part.id,
                            },
                          },
                        });

                        handleRemove(part.id);
                      }}
                    >
                      This change will delete the part. Please confirm.
                    </ConfirmationModal>,
                  );
                }}
              />
            </Link>
          ))}
          {createPortal(
            <DragOverlay dropAnimation={dropAnimationConfig}>
              {activeId && activeItem ? (
                <SortableTreeItem
                  id={activeId}
                  disabled={true}
                  depth={activeItem.depth}
                  clone
                  childCount={getChildCount(items, activeId) + 1}
                  value={
                    <PartTreeItem
                      part={activeItem}
                      setInputFocusing={setInputFocusing}
                      canEdit={false}
                      onCreated={() => resetShowAddPart()}
                      newPartId={newPartId}
                      inverseDepth={maxDepth - activeItem.depth}
                      indentationWidth={indentationWidth}
                    />
                  }
                  indentationWidth={indentationWidth}
                />
              ) : null}
            </DragOverlay>,
            document.body,
          )}
        </SortableContext>
      </DndContext>
    );

    function handleDragStart({ active: { id: activeId } }: DragStartEvent) {
      setActiveId(activeId);
      setOverId(activeId);
      document.body.style.setProperty("cursor", "grabbing");
    }

    function handleDragMove({ delta }: DragMoveEvent) {
      setOffsetLeft(delta.x);
    }

    function handleDragOver({ over }: DragOverEvent) {
      setOverId(over?.id ?? null);
    }

    function handleDragEnd({ active, over }: DragEndEvent) {
      resetState();

      if (projected && over) {
        const { depth, parentId } = projected;
        const clonedItems: FlattenedItem[] = JSON.parse(
          JSON.stringify(flattenTree(items)),
        );
        const overIndex = clonedItems.findIndex(({ id }) => id === over.id);
        const activeIndex = clonedItems.findIndex(({ id }) => id === active.id);
        const activeTreeItem = clonedItems[activeIndex];

        clonedItems[activeIndex] = { ...activeTreeItem, depth, parentId };
        const sortedItems = arrayMove(clonedItems, activeIndex, overIndex);
        const newItems = buildTree(sortedItems);

        const leftOrdinal =
          overIndex === 0 ? null : sortedItems[overIndex - 1].ordinal;
        const rightOrdinal =
          overIndex === sortedItems.length - 1
            ? null
            : sortedItems[overIndex + 1].ordinal;
        const ordinal = getFractionalIndex(leftOrdinal, rightOrdinal);

        sortedItems[overIndex].ordinal = ordinal;
        setItems(newItems);

        if (parentId !== activeTreeItem.parentId) {
          window.setTimeout(
            () =>
              confirmParentChange({
                partId: active.id.toString(),
                parentId: parentId?.toString() ?? null,
                ordinal,
              }),
            300, // this is an awful magic number that I don't know why it works, but it does
          );
        } else if (overIndex !== activeIndex) {
          updatePart({
            payload: {
              ordinal,
            },
            partId: active.id.toString(),
          });
        }
      }
    }

    function handleDragCancel() {
      resetState();
    }

    function resetState() {
      setOverId(null);
      setActiveId(null);
      setOffsetLeft(0);

      document.body.style.setProperty("cursor", "");
    }

    function handleRemove(id: UniqueIdentifier) {
      setItems((items) => removeItem(items, id));
    }

    function handleCollapse(id: UniqueIdentifier) {
      setItems((items) =>
        setProperty(items, id, "collapsed", (value) => {
          return !value;
        }),
      );
    }

    function expandOrCollapseAll({ expand }: { expand: boolean }) {
      flattenTree(items).forEach((part) => {
        setItems((items) =>
          setProperty(items, part.id, "collapsed", () => !expand),
        );
      });
    }
  },
);

export default SortablePartTree;

export type SortablePartTreeRef = {
  expandOrCollapseAll: ({ expand }: { expand: boolean }) => void;
};
